import { Ref } from 'vue';

export type MultiLineBreakMode = 'br-tags' | 'newlines' | 'break-spans';

export interface MultiLineBreakOptions {
  mode?: MultiLineBreakMode;
}

const SPAN_BREAK = '<span class="bcx-multiline-break"></span>';
const SPAN_EMPTY_LINE_BREAK = '<span class="bcx-multiline-break bcx-multiline-break--empty-line"></span>';

const getMode = (useBrOrOptions: boolean | MultiLineBreakOptions = false): MultiLineBreakMode => {
  if (typeof useBrOrOptions === 'boolean') return useBrOrOptions ? 'br-tags' : 'newlines';
  return useBrOrOptions.mode ?? 'newlines';
};

const getLineBreakReplacement = (mode: MultiLineBreakMode) => {
  switch (mode) {
    case 'br-tags':
      return '<br>';
    case 'break-spans':
      return SPAN_BREAK;
    case 'newlines':
    default:
      return '\n';
  }
};

const replaceHtmlBreaksWithSpans = (text: string) => text.replace(/(?:<br\s*\/?>\s*)+/gi, (match) => {
  const breakCount = match.match(/<br\s*\/?>/gi)?.length ?? 0;
  return Array.from({ length: breakCount }, () => SPAN_BREAK).join('');
});

const replaceConsecutiveSpanBreaks = (text: string) => text.replace(
  /(?:<span class="bcx-multiline-break"><\/span>){2,}/g,
  (match) => {
    const breakCount = match.match(/<span class="bcx-multiline-break"><\/span>/g)?.length ?? 0;
    return SPAN_BREAK + Array.from({ length: breakCount - 1 }, () => SPAN_EMPTY_LINE_BREAK).join('');
  }
);

const useMultiLineBreaks = (lineBreaks: Record<string, string>, currentLineBreak: Ref<string>) => {
  const tl = (text: string, useBrOrOptions: boolean | MultiLineBreakOptions = false) => {
    if (!text || typeof text !== 'string') return '';
    const mode = getMode(useBrOrOptions);
    const lineBreakReplacement = getLineBreakReplacement(mode);
    Object.entries(lineBreaks).forEach(([key, lineBreak]) => {
      text = text.replaceAll(lineBreak, key === currentLineBreak.value ? lineBreakReplacement : '');
    });
    if (mode === 'break-spans') return replaceConsecutiveSpanBreaks(replaceHtmlBreaksWithSpans(text));
    return text;
  };

  return { tl };
};

export default useMultiLineBreaks;
